import { useState, useCallback } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useLog } from '../../contexts/LogContext';
import { api, uploadFile } from '../../utils/api';
import { transformDiagramItems } from '../../utils/transforms';
import { slugify } from '../../utils/slugify';
import {
  validateImageFile,
  getImageNaturalDimensions,
} from '../../utils/fileHandler';
import { needsClientProcessing } from '../../utils/compressImage';
import { compressImageOnServer } from '../../utils/compressImageOnServer';
import { MAX_WEBP_DIMENSION, MAX_AUTO_DIMENSION } from '../../constants';
import { JsonFileInput } from '../../components/common/JsonFileInput';
import { DiagramPreview } from '../../components/DiagramPreview';
import { ActionButtons } from '../../components/common/ActionButtons';
import type {
  RawDiagramItem,
  DiagramFileSettings,
  DiagramPageImageInfo,
  ImportDiagramPageInfo,
  ImportDiagramPdfInfo,
} from '../../types/diagram.types';
import { EMPTY_DIAGRAM_FILE_SETTINGS } from '../../types/diagram.types';
import type { DiagramCompressionSettings } from '../../components/DiagramSummaryItem/types';
import type { UploadResponse } from '../../utils/api';
import type { DiagramsContainerProps } from './types';

export const DiagramsContainer = ({ projectId }: DiagramsContainerProps) => {
  const { token } = useConfig();
  const { log, clearLog } = useLog();

  const [diagramData, setDiagramData] = useState<RawDiagramItem[] | null>(null);
  const [diagramFileSettingsMap, setDiagramFileSettingsMap] = useState<
    Record<string, DiagramFileSettings>
  >({});
  const [name, setName] = useState('');
  const [running, setRunning] = useState(false);

  const [diagramFileInfo, setDiagramFileInfo] = useState<{
    fileName: string;
    stats: string;
  } | null>(null);

  // ── Handle diagram JSON load ──
  const handleDiagramLoaded = useCallback((data: unknown, fileName: string) => {
    const json = data as RawDiagramItem[] | { items: RawDiagramItem[] };
    const items = Array.isArray(json)
      ? json
      : (json as { items: RawDiagramItem[] }).items || [];
    setDiagramData(items);

    // Count unique diagrams
    const diagKeys = new Set<string>();
    items.forEach((item) =>
      (item.diagrams || []).forEach((d) => {
        diagKeys.add(typeof d.diagram === 'string' ? d.diagram : d.diagram.id);
      }),
    );

    setDiagramFileInfo({
      fileName,
      stats: `${items.length} item(s) | ${diagKeys.size} diagram(s)`,
    });

    // Auto-prefill name from filename, stripping the .json extension.
    const fileNameWithoutExtension = fileName.replace(/\.json$/i, "");
    setName(slugify(fileNameWithoutExtension));
    setDiagramFileSettingsMap({});
  }, []);

  // ── Auto-compute compression settings for a diagram based on its page images ──
  const computeAutoCompressionSettings = useCallback(
    (pageImages: Record<number, DiagramPageImageInfo | null>) => {
      let maxDimension = 0;
      for (const imageInfo of Object.values(pageImages)) {
        if (!imageInfo) continue;
        maxDimension = Math.max(
          maxDimension,
          imageInfo.naturalWidth,
          imageInfo.naturalHeight,
        );
      }

      if (maxDimension === 0) {
        return { scaleFactor: 1, quality: 100, outputFormat: 'webp' as const };
      }

      if (maxDimension > MAX_AUTO_DIMENSION) {
        const autoScaleFactor = MAX_AUTO_DIMENSION / maxDimension;
        return {
          scaleFactor: parseFloat(autoScaleFactor.toFixed(4)),
          quality: 90,
          outputFormat: 'webp' as const,
        };
      }

      return { scaleFactor: 1, quality: 100, outputFormat: 'webp' as const };
    },
    [],
  );

  // ── Update page image for a diagram ──
  const handlePageImageChange = useCallback(
    (
      diagramKey: string,
      pageIndex: number,
      imageInfo: DiagramPageImageInfo | null,
    ) => {
      setDiagramFileSettingsMap((prev) => {
        const current = prev[diagramKey] ?? EMPTY_DIAGRAM_FILE_SETTINGS;
        const updatedPageImages = {
          ...current.pageImages,
          [pageIndex]: imageInfo,
        };
        const autoSettings = computeAutoCompressionSettings(updatedPageImages);
        return {
          ...prev,
          [diagramKey]: {
            ...current,
            pageImages: updatedPageImages,
            ...autoSettings,
          },
        };
      });
    },
    [computeAutoCompressionSettings],
  );

  // ── Update compression settings for a diagram (manual override) ──
  const handleCompressionSettingsChange = useCallback(
    (diagramKey: string, settings: Partial<DiagramCompressionSettings>) => {
      setDiagramFileSettingsMap((prev) => {
        const current = prev[diagramKey] ?? EMPTY_DIAGRAM_FILE_SETTINGS;
        return {
          ...prev,
          [diagramKey]: { ...current, ...settings },
        };
      });
    },
    [],
  );

  // ── Update PDF for a diagram ──
  const handlePdfChange = useCallback(
    (diagramKey: string, file: File | null) => {
      setDiagramFileSettingsMap((prev) => {
        const current = prev[diagramKey] ?? EMPTY_DIAGRAM_FILE_SETTINGS;
        return {
          ...prev,
          [diagramKey]: { ...current, pdfFile: file },
        };
      });
    },
    [],
  );

  // ── Handle folder selection for auto-fill ──
  const handleFolderSelect = useCallback(
    async (files: FileList) => {
      if (!diagramData || diagramData.length === 0) {
        log('err', 'Please load a diagram JSON file first');
        return;
      }

      // Build a set of known diagram keys from loaded data
      const knownDiagramKeys = new Set<string>();
      for (const item of diagramData) {
        for (const diagramRef of item.diagrams || []) {
          const key =
            typeof diagramRef.diagram === 'string'
              ? diagramRef.diagram
              : diagramRef.diagram.id;
          knownDiagramKeys.add(key);
        }
      }

      let matchedCount = 0;
      let skippedCount = 0;
      const matchedDiagrams = new Set<string>();
      const updates: Record<string, Record<number, DiagramPageImageInfo>> = {};

      for (const file of Array.from(files)) {
        const relativePath = file.webkitRelativePath;
        const segments = relativePath.split('/');

        // Expect: root_folder/diagramKey/page_X.png (or deeper nesting)
        if (segments.length < 3) {
          skippedCount++;
          continue;
        }

        const fileName = segments[segments.length - 1];
        const folderName = segments[segments.length - 2];

        // Check if folder name matches a known diagram key
        if (!knownDiagramKeys.has(folderName)) {
          skippedCount++;
          continue;
        }

        // Extract page index from filename (e.g., page_3.png → 3)
        const pageMatch = fileName.match(/page_(\d+)/);
        if (!pageMatch) {
          skippedCount++;
          continue;
        }

        // Validate image type
        const validationError = validateImageFile(file);
        if (validationError) {
          skippedCount++;
          continue;
        }

        const pageIndex = parseInt(pageMatch[1], 10);
        if (!updates[folderName]) updates[folderName] = {};

        const dimensions = await getImageNaturalDimensions(file);
        updates[folderName][pageIndex] = {
          file,
          naturalWidth: dimensions.width,
          naturalHeight: dimensions.height,
        };

        matchedCount++;
        matchedDiagrams.add(folderName);
      }

      setDiagramFileSettingsMap((prev) => {
        const next = { ...prev };
        for (const [diagramKey, pageImages] of Object.entries(updates)) {
          const current = next[diagramKey] ?? EMPTY_DIAGRAM_FILE_SETTINGS;
          const updatedPageImages = { ...current.pageImages, ...pageImages };
          const autoSettings =
            computeAutoCompressionSettings(updatedPageImages);
          next[diagramKey] = {
            ...current,
            pageImages: updatedPageImages,
            ...autoSettings,
          };
        }
        return next;
      });

      log(
        'ok',
        `Folder auto-fill: matched ${matchedCount} file(s) across ${matchedDiagrams.size} diagram(s), skipped ${skippedCount} file(s).`,
      );
    },
    [diagramData, log, computeAutoCompressionSettings],
  );

  // ── Upload diagram page images, return S3 keys map ──
  const uploadDiagramPageFiles = useCallback(async (): Promise<Record<
    string,
    string
  > | null> => {
    if (!projectId) {
      log('err', 'Project ID is required for image upload');
      return null;
    }

    const imageEntries: {
      diagramKey: string;
      pageIndex: number;
      file: File;
    }[] = [];
    for (const [diagramKey, settings] of Object.entries(
      diagramFileSettingsMap,
    )) {
      for (const [pageIndex, imageInfo] of Object.entries(
        settings.pageImages,
      )) {
        if (imageInfo) {
          imageEntries.push({
            diagramKey,
            pageIndex: Number(pageIndex),
            file: imageInfo.file,
          });
        }
      }
    }

    if (imageEntries.length === 0) {
      log('info', 'No diagram page image files selected — skipping upload.');
      return {};
    }

    // Validate WebP dimension limits before any server calls
    for (const [diagramKey, settings] of Object.entries(
      diagramFileSettingsMap,
    )) {
      if (settings.outputFormat !== 'webp') continue;
      for (const [pageIndex, imageInfo] of Object.entries(
        settings.pageImages,
      )) {
        if (!imageInfo || !imageInfo.naturalWidth || !imageInfo.naturalHeight)
          continue;
        const outputWidth = Math.round(
          imageInfo.naturalWidth * settings.scaleFactor,
        );
        const outputHeight = Math.round(
          imageInfo.naturalHeight * settings.scaleFactor,
        );
        if (
          outputWidth > MAX_WEBP_DIMENSION ||
          outputHeight > MAX_WEBP_DIMENSION
        ) {
          log(
            'err',
            `Diagram "${diagramKey}" page ${pageIndex}: WebP output would be ${outputWidth}x${outputHeight} px, exceeding the ${MAX_WEBP_DIMENSION}px WebP limit. Reduce scale factor or switch format.`,
          );
          return null;
        }
      }
    }

    imageEntries.sort((a, b) =>
      a.diagramKey === b.diagramKey
        ? a.pageIndex - b.pageIndex
        : a.diagramKey.localeCompare(b.diagramKey),
    );

    log(
      'step',
      `Uploading ${imageEntries.length} diagram page image(s) to S3...`,
    );

    const imageS3Keys: Record<string, string> = {};

    for (const { diagramKey, pageIndex, file } of imageEntries) {
      const slugifiedKey = slugify(diagramKey);
      const diagramSettings =
        diagramFileSettingsMap[diagramKey] ?? EMPTY_DIAGRAM_FILE_SETTINGS;
      const { scaleFactor, quality, outputFormat } = diagramSettings;

      const willProcess = needsClientProcessing(
        { scaleFactor, quality, outputFormat },
        file.type,
      );

      let fileToUpload: File = file;

      if (willProcess) {
        log(
          'info',
          `  Compressing ${slugifiedKey} page ${pageIndex} on server (scale=${scaleFactor}, quality=${quality}, format=${outputFormat})...`,
        );

        try {
          const result = await compressImageOnServer({
            file,
            scaleFactor,
            quality,
            outputFormat,
          });
          fileToUpload = result.file;

          log(
            'info',
            `  ${slugifiedKey} page ${pageIndex}: ${result.originalWidth}x${result.originalHeight} -> ${result.outputWidth}x${result.outputHeight} | ${(file.size / (1024 * 1024)).toFixed(2)} MB -> ${(result.blob.size / (1024 * 1024)).toFixed(2)} MB`,
          );
        } catch (err) {
          log(
            'err',
            `  ${slugifiedKey} page ${pageIndex} compression failed: ${(err as Error).message}`,
          );
          return null;
        }
      } else {
        log(
          'info',
          `  Uploading ${slugifiedKey} page ${pageIndex} as-is: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
        );
      }

      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('projectId', projectId);
      formData.append('diagramKey', slugifiedKey);
      formData.append('pageIndex', String(pageIndex));

      try {
        const res = await uploadFile<UploadResponse>(
          '/api/quantity-surveyor-diagram-pages/upload-page-image',
          formData,
          token,
        );

        if (res.status === 200 || res.status === 201) {
          const s3Key = res.data.s3Key;
          imageS3Keys[`${diagramKey}::${pageIndex}`] = s3Key;
          log('ok', `  ${slugifiedKey} page ${pageIndex} uploaded -> ${s3Key}`);
        } else {
          log(
            'err',
            `  ${slugifiedKey} page ${pageIndex} failed (${res.status}): ${JSON.stringify(res.data)}`,
          );
          return null;
        }
      } catch (err) {
        log(
          'err',
          `  ${slugifiedKey} page ${pageIndex} upload error: ${(err as Error).message}`,
        );
        return null;
      }
    }

    log(
      'ok',
      `All ${imageEntries.length} diagram page image(s) uploaded successfully.`,
    );
    return imageS3Keys;
  }, [projectId, diagramFileSettingsMap, token, log]);

  // ── Upload diagram PDFs, return S3 keys map ──
  const uploadDiagramPdfs = useCallback(async (): Promise<Record<
    string,
    string
  > | null> => {
    if (!projectId) {
      log('err', 'Project ID is required for PDF upload');
      return null;
    }

    const pdfEntries: { diagramKey: string; file: File }[] = [];
    for (const [diagramKey, settings] of Object.entries(
      diagramFileSettingsMap,
    )) {
      if (settings.pdfFile) {
        pdfEntries.push({ diagramKey, file: settings.pdfFile });
      }
    }

    if (pdfEntries.length === 0) {
      log('info', 'No diagram PDF files selected — skipping PDF upload.');
      return {};
    }

    log('step', `Uploading ${pdfEntries.length} diagram PDF(s) to S3...`);

    const pdfS3Keys: Record<string, string> = {};

    for (const { diagramKey, file } of pdfEntries) {
      const slugifiedKey = slugify(diagramKey);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      formData.append('diagramKey', slugifiedKey);

      log('info', `  Uploading PDF for diagram "${diagramKey}"...`);

      try {
        const res = await uploadFile<UploadResponse>(
          '/api/quantity-surveyor-diagram-pages/upload-diagram-pdf',
          formData,
          token,
        );

        if (res.status === 200 || res.status === 201) {
          pdfS3Keys[diagramKey] = res.data.s3Key;
          log('ok', `  PDF for "${diagramKey}" uploaded → ${res.data.s3Key}`);
        } else {
          log(
            'err',
            `  PDF for "${diagramKey}" failed (${res.status}): ${JSON.stringify(res.data)}`,
          );
          return null;
        }
      } catch (err) {
        log(
          'err',
          `  PDF for "${diagramKey}" upload error: ${(err as Error).message}`,
        );
        return null;
      }
    }

    log('ok', `All ${pdfEntries.length} diagram PDF(s) uploaded successfully.`);
    return pdfS3Keys;
  }, [projectId, diagramFileSettingsMap, token, log]);

  // ── Build pages and pdfs arrays for import payload ──
  const buildImportFileMetadata = useCallback(
    (
      imageS3Keys: Record<string, string>,
      pdfS3Keys: Record<string, string>,
    ): { pages: ImportDiagramPageInfo[]; pdfs: ImportDiagramPdfInfo[] } => {
      const pages: ImportDiagramPageInfo[] = [];
      const pdfs: ImportDiagramPdfInfo[] = [];

      // Build pages from image S3 keys
      // Key format: "diagramKey::pageIndex", value: full S3 path
      for (const [compositeKey, imagePath] of Object.entries(imageS3Keys)) {
        const [diagramKey, pageIndexStr] = compositeKey.split('::');
        const pageIndex = parseInt(pageIndexStr, 10);
        // Extract the actual filename from the S3 key (e.g., "page_0.webp")
        const pageFilename = imagePath.split('/').pop()!;
        pages.push({
          diagramKey,
          pageFilename,
          imagePath,
          pageIndex,
        });
      }

      // Build pdfs from PDF S3 keys
      for (const [diagramKey, pdfPath] of Object.entries(pdfS3Keys)) {
        pdfs.push({ diagramKey, pdfPath });
      }

      return { pages, pdfs };
    },
    [],
  );

  // ── Build pages and pdfs from convention (for "import data only" mode) ──
  const buildConventionFileMetadata = useCallback((): {
    pages: ImportDiagramPageInfo[];
    pdfs: ImportDiagramPdfInfo[];
  } => {
    const pages: ImportDiagramPageInfo[] = [];
    const pdfs: ImportDiagramPdfInfo[] = [];

    if (!diagramData || !projectId) return { pages, pdfs };

    const seenDiagrams = new Set<string>();

    for (const item of diagramData) {
      for (const diagramRef of item.diagrams || []) {
        const diagramKey =
          typeof diagramRef.diagram === 'string'
            ? diagramRef.diagram
            : diagramRef.diagram.id;
        const slugifiedKey = slugify(diagramKey);

        // Add PDF entry once per diagram
        if (!seenDiagrams.has(diagramKey)) {
          seenDiagrams.add(diagramKey);
          pdfs.push({
            diagramKey,
            pdfPath: `smart-surveyor/projects/${projectId}/distribution_board_files/${slugifiedKey}/source/${slugifiedKey}.pdf`,
          });
        }

        // Add page entries
        for (const page of diagramRef.pages || []) {
          const pageMatch = page.page.match(/page_(\d+)/);
          const pageIndex = pageMatch ? parseInt(pageMatch[1], 10) : 0;
          pages.push({
            diagramKey,
            pageFilename: page.page,
            imagePath: `smart-surveyor/projects/${projectId}/distribution_board_files/${slugifiedKey}/source/${page.page}`,
            pageIndex,
          });
        }
      }
    }

    return { pages, pdfs };
  }, [diagramData, projectId]);

  // ── Helper: check if any files are selected ──
  const hasAnyImageFiles = useCallback(() => {
    return Object.values(diagramFileSettingsMap).some((settings) =>
      Object.values(settings.pageImages).some(
        (imageInfo) => imageInfo !== null,
      ),
    );
  }, [diagramFileSettingsMap]);

  const hasAnyPdfFiles = useCallback(() => {
    return Object.values(diagramFileSettingsMap).some(
      (settings) => settings.pdfFile !== null,
    );
  }, [diagramFileSettingsMap]);

  // ── Upload files only ──
  const handleUploadOnly = useCallback(async () => {
    if (running) return;
    if (!projectId) return log('err', 'Project ID is required');
    if (!diagramData || diagramData.length === 0)
      return log('err', 'Please load a diagram JSON file first');

    if (!hasAnyImageFiles() && !hasAnyPdfFiles())
      return log('err', 'No files selected. Select image or PDF files above.');

    setRunning(true);
    clearLog();

    try {
      log('step', 'Upload-only mode: uploading files to S3...');
      const imageResult = await uploadDiagramPageFiles();
      const pdfResult = await uploadDiagramPdfs();
      if (imageResult !== null && pdfResult !== null) {
        log('ok', '\nDone! Files uploaded successfully.');
      } else {
        log('err', '\nUpload failed. Check the errors above.');
      }
    } catch (err) {
      log('err', `Unexpected error: ${(err as Error).message}`);
    } finally {
      setRunning(false);
    }
  }, [
    running,
    projectId,
    diagramData,
    hasAnyImageFiles,
    hasAnyPdfFiles,
    clearLog,
    log,
    uploadDiagramPageFiles,
    uploadDiagramPdfs,
  ]);

  // ── Execute diagram import ──
  const handleExecute = useCallback(async () => {
    if (running) return;

    if (!diagramData || diagramData.length === 0)
      return log('err', 'Please load a diagram JSON file');
    if (!projectId) return log('err', 'Project ID is required');

    const trimmedName = name.trim();
    if (!trimmedName) return log('err', 'Name is required');

    const base = '/api/quantity-surveyor-distribution-board-files';

    setRunning(true);
    clearLog();

    try {
      const hasImages = hasAnyImageFiles();
      const hasPdfs = hasAnyPdfFiles();
      const hasFiles = hasImages || hasPdfs;
      const totalSteps = hasFiles ? 3 : 1;
      let step = 0;

      let imageS3Keys: Record<string, string> = {};
      if (hasImages) {
        step++;
        log(
          'step',
          `[${step}/${totalSteps}] Uploading diagram page images to S3...`,
        );
        const result = await uploadDiagramPageFiles();
        if (result === null) {
          log('err', 'Image upload failed — aborting.');
          return;
        }
        imageS3Keys = result;
      }

      let pdfS3Keys: Record<string, string> = {};
      if (hasPdfs) {
        step++;
        log('step', `[${step}/${totalSteps}] Uploading diagram PDFs to S3...`);
        const result = await uploadDiagramPdfs();
        if (result === null) {
          log('err', 'PDF upload failed — aborting.');
          return;
        }
        pdfS3Keys = result;
      }

      step++;
      log(
        'step',
        `[${step}/${totalSteps}] Creating distribution board file and importing diagram items...`,
      );
      const payload = transformDiagramItems(diagramData);
      const { pages, pdfs } = buildImportFileMetadata(imageS3Keys, pdfS3Keys);
      const importPayload = {
        projectId: parseInt(projectId, 10),
        name: trimmedName,
        import: { ...payload, pages, pdfs },
      };
      log('info', `  Sending ${payload.items.length} item(s)...`);
      const importRes = await api(
        'POST',
        `${base}/create-and-import`,
        importPayload,
        token,
      );
      if (importRes.status !== 201 && importRes.status !== 200) {
        log(
          'err',
          `  Failed (${importRes.status}): ${JSON.stringify(importRes.data)}`,
        );
        return;
      }
      const result = importRes.data as { distributionBoardFileId: number };
      log('ok', `  ${JSON.stringify(importRes.data)}`);

      log(
        'ok',
        `\nDone! Distribution board file ${result.distributionBoardFileId} created and diagram data imported.`,
      );
    } catch (err) {
      log('err', `Unexpected error: ${(err as Error).message}`);
    } finally {
      setRunning(false);
    }
  }, [
    running,
    diagramData,
    projectId,
    name,
    hasAnyImageFiles,
    hasAnyPdfFiles,
    token,
    clearLog,
    log,
    uploadDiagramPageFiles,
    uploadDiagramPdfs,
    buildImportFileMetadata,
  ]);

  // ── Import data to database only (no S3 upload) ──
  // Constructs S3 paths from convention — user must have uploaded files manually
  const handleImportDataOnly = useCallback(async () => {
    if (running) return;

    if (!diagramData || diagramData.length === 0) {
      return log('err', 'Please load a diagram JSON file');
    }
    if (!projectId) return log('err', 'Project ID is required');

    const trimmedName = name.trim();
    if (!trimmedName) return log('err', 'Name is required');

    const base = '/api/quantity-surveyor-distribution-board-files';
    const { pages, pdfs } = buildConventionFileMetadata();

    setRunning(true);
    clearLog();

    try {
      log(
        'step',
        'Creating distribution board file and importing diagram items...',
      );
      const payload = transformDiagramItems(diagramData);
      const importPayload = {
        projectId: parseInt(projectId, 10),
        name: trimmedName,
        import: { ...payload, pages, pdfs },
      };
      log('info', `  Sending ${payload.items.length} item(s)...`);
      const importResponse = await api(
        'POST',
        `${base}/create-and-import`,
        importPayload,
        token,
      );
      if (importResponse.status !== 201 && importResponse.status !== 200) {
        log(
          'err',
          `  Failed (${importResponse.status}): ${JSON.stringify(importResponse.data)}`,
        );
        return;
      }
      const result = importResponse.data as {
        distributionBoardFileId: number;
      };
      log('ok', `  ${JSON.stringify(importResponse.data)}`);
      log(
        'ok',
        `\nDone! Distribution board file ${result.distributionBoardFileId} created and diagram data imported (no files uploaded).`,
      );
    } catch (error) {
      log('err', `Unexpected error: ${(error as Error).message}`);
    } finally {
      setRunning(false);
    }
  }, [
    running,
    diagramData,
    projectId,
    name,
    token,
    clearLog,
    log,
    buildConventionFileMetadata,
  ]);

  return (
    <div>
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
        <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
          Diagram JSON File
        </h2>
        <div className="flex gap-3 flex-wrap">
          <JsonFileInput
            label="Diagram Items JSON (array of items with diagrams/pages)"
            placeholder="Drop or click to select diagram JSON"
            onFileLoaded={handleDiagramLoaded}
            onError={(msg) => log('err', msg)}
            loadedInfo={diagramFileInfo}
          />
        </div>
      </div>

      {diagramData && diagramData.length > 0 && (
        <DiagramPreview
          diagramData={diagramData}
          projectId={projectId}
          diagramFileSettingsMap={diagramFileSettingsMap}
          onPageImageChange={handlePageImageChange}
          onCompressionSettingsChange={handleCompressionSettingsChange}
          onPdfChange={handlePdfChange}
          name={name}
          onNameChange={setName}
          onFolderSelect={handleFolderSelect}
        />
      )}

      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
        <ActionButtons
          running={running}
          onUploadFiles={handleUploadOnly}
          onPrimaryAction={handleExecute}
          primaryLabel="Create & Import"
          showPrimary
          onImportDataOnly={handleImportDataOnly}
          showImportDataOnly
        />
      </div>
    </div>
  );
};
